import { createApi } from '@reduxjs/toolkit/query/react'
import { EDIT_PRIVATE_CLIENT_DETAILS, FAVOURITE_CLIENT_REPORT, FAVOURITE_PRIVATE_CLIENT, PRIVATE_CLIENT_ARCHIVE, PRIVATE_CLIENT_CREATE, PRIVATE_CLIENT_DELETE, PRIVATE_CLIENT_DETAILS, PRIVATE_CLIENT_LIST } from "config/apiPaths"
import { RTK_CACHE_EXPIRATION } from "config/constants";
import moment from 'moment';
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const privateClientApi = createApi({
    reducerPath: 'privateClientApi',
    baseQuery: axiosBaseQuery({
        baseUrl: "/client/private",
    }),
    tagTypes: ["PrivatClientApi", "PrivatClientDetailsApi"],
    keepUnusedDataFor: RTK_CACHE_EXPIRATION,
    endpoints: (builder) => ({

        // get private client list
        getPrivateClientList: builder.query({
            query: ({ search = "", therapist_id = "", status = "", start_date = "", end_date = "", page = 1, limit = 10, funding_source = "", sort = { column: "id", direction: "ASC" } }) => ({
                method: "PATCH",
                url: PRIVATE_CLIENT_LIST,
                data: { search, therapist_id, status: status?.value || "", page, limit, funding_source: funding_source?.value || "", start_date: start_date ? moment(start_date).format("YYYY-MM-DD") : "", end_date: end_date ? moment(end_date).format("YYYY-MM-DD") : "", sort }
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["PrivatClientApi"]
        }),

        // delete private client
        deletePrivateClient: builder.mutation({
            query: (id) => ({
                method: "DELETE",
                url: `${PRIVATE_CLIENT_DELETE}/${id}`
            }),
            invalidatesTags: ["PrivatClientApi", "PrivatClientDetailsApi"]
        }),

        // create private client
        createPrivateClient: builder.mutation({
            query: (data) => ({
                method: "POST",
                url: PRIVATE_CLIENT_CREATE,
                data
            }),
            invalidatesTags: ["PrivatClientApi"]
        }),

        // private client details
        privateClientDetails: builder.query({
            query: (id) => ({
                method: "GET",
                url: `${PRIVATE_CLIENT_DETAILS}/${id}`
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["PrivatClientDetailsApi"]
        }),

        // update private client
        updatePrivateClient: builder.mutation({
            query: ({ data, clientId }) => ({
                method: "PUT",
                url: `${EDIT_PRIVATE_CLIENT_DETAILS}/${clientId}`,
                data
            }),
            invalidatesTags: ["PrivatClientDetailsApi", "PrivatClientApi"]
        }),

        // archive private client
        archivePrivateClient: builder.mutation({
            query: (id) => ({
                method: "DELETE",
                url: `${PRIVATE_CLIENT_ARCHIVE}/${id}`
            }),
            invalidatesTags: ["PrivatClientApi", "PrivatClientDetailsApi"]
        }),

        // add links to private client
        shareLinks: builder.mutation({
            query: ({ clientId, data }) => ({
                method: "PUT",
                url: `/shared-links/${clientId}`,
                data
            }),
            invalidatesTags: ["PrivatClientDetailsApi"],
            transformResponse: (response) => response?.data,
        }),

        updatePrivateClientStatus: builder.mutation({
            query: ({ clientId, status }) => ({
                method: "GET",
                url: `/${clientId}/${status}`,
            }),
            invalidatesTags: ["PrivatClientDetailsApi", "PrivatClientApi"],
            transformResponse: (response) => response?.data,
        }),

        favouritePrivateClient: builder.mutation({
            query: ({ data, id }) => ({
                method: "PATCH",
                url: `${FAVOURITE_PRIVATE_CLIENT}/${id}`,
                data
            }),
            invalidatesTags: ["PrivatClientDetailsApi", "PrivatClientApi"]
        }),
    })
})

export const {
    useGetPrivateClientListQuery,
    useDeletePrivateClientMutation,
    useCreatePrivateClientMutation,
    usePrivateClientDetailsQuery,
    useUpdatePrivateClientMutation,
    useArchivePrivateClientMutation,
    useLazyPrivateClientDetailsQuery,
    useShareLinksMutation,
    useUpdatePrivateClientStatusMutation,
    useFavouritePrivateClientMutation
} = privateClientApi;